import React, { createContext, useContext, useState, useEffect, useRef, useCallback } from 'react';
import {
  createUserWithEmailAndPassword,
  signInWithEmailAndPassword,
  signInWithPopup,
  signInWithCredential,
  signOut,
  onAuthStateChanged,
  updateProfile,
  sendEmailVerification,
  sendPasswordResetEmail,
  linkWithPopup,
  linkWithCredential,
  unlink,
  reauthenticateWithPopup,
  reauthenticateWithCredential,
  revokeAccessToken,
  EmailAuthProvider,
  GoogleAuthProvider,
  OAuthProvider,
  AuthProvider as ProveedorFirebase,
  AuthCredential,
  UserCredential,
  User
} from 'firebase/auth';
import { doc } from 'firebase/firestore';
import { updateDoc } from '../config/escrituras';
import { auth, db, borrarCacheLocal } from '../config/firebase';
import { URL_PUBLICA } from '../config/marca';
import { esApp } from '../utils/plataforma';
import { desactivarNotificaciones } from '../utils/notificaciones';
import { cancelarRecordatorios } from '../utils/recordatoriosGastos';
import type { ResultadoNativo } from '../utils/accesoNativo';

// El login nativo solo existe en la app: se carga aparte para no pesar en la web
const credencialNativa = async (p: ProveedorSocial): Promise<ResultadoNativo> =>
  (await import('../utils/accesoNativo')).credencialNativa(p);
const revocarAppleNativo = async (u: User, codigo: string): Promise<void> =>
  (await import('../utils/accesoNativo')).revocarAppleNativo(u, codigo);

export type ProveedorSocial = 'google' | 'apple';

/** Id que Firebase da a cada método en `user.providerData`. */
export const ID_PROVEEDOR: Record<ProveedorSocial, string> = {
  google: 'google.com',
  apple: 'apple.com',
};

export const NOMBRE_PROVEEDOR: Record<ProveedorSocial, string> = {
  google: 'Google',
  apple: 'Apple',
};

/** Con qué se vuelve a identificar alguien antes de algo delicado (borrar la cuenta). */
export type MetodoReautenticacion = ProveedorSocial | 'password';

function crearProveedor(p: ProveedorSocial): ProveedorFirebase {
  if (p === 'google') {
    const g = new GoogleAuthProvider();
    // Sin esto, quien tiene varias cuentas de Google entra con la última sin preguntar
    g.setCustomParameters({ prompt: 'select_account' });
    return g;
  }
  const a = new OAuthProvider('apple.com');
  a.addScope('email');
  a.addScope('name');
  a.setCustomParameters({ locale: 'es_ES' });
  return a;
}

/**
 * Alguien intentó entrar con Apple o Google y ya tenía cuenta con ese email
 * por otro método. Se guarda la credencial para vincularla en cuanto entre
 * como lo hacía hasta ahora. Solo vive en memoria: si recarga, se pierde y
 * tendrá que volver a pulsar el botón, nada más.
 */
export interface VinculacionPendiente {
  email: string;
  proveedor: ProveedorSocial;
  credencial: AuthCredential;
}

/**
 * Dónde vive la función que borra la cuenta en el servidor (Netlify). Con la
 * URL pública delante: dentro de la app de iOS una ruta relativa iría a
 * capacitor://localhost, que no es el servidor.
 */
const URL_BORRAR_CUENTA = `${URL_PUBLICA}/.netlify/functions/borrar-cuenta`;

interface AuthContextType {
  user: User | null;
  loading: boolean;
  signup: (email: string, password: string) => Promise<void>;
  login: (email: string, password: string) => Promise<void>;
  /** Entrar (o registrarse, es lo mismo) con Apple o Google. */
  entrarCon: (proveedor: ProveedorSocial) => Promise<void>;
  vinculacionPendiente: VinculacionPendiente | null;
  cancelarVinculacion: () => void;
  /** Añadir Apple o Google a la cuenta con la que ya se ha entrado. */
  vincular: (proveedor: ProveedorSocial) => Promise<void>;
  /** Quitar un método de acceso. Nunca el último. */
  desvincular: (providerId: string) => Promise<void>;
  metodoReautenticacion: () => MetodoReautenticacion | null;
  /** Borra la cuenta y todos sus datos. Pide volver a identificarse antes. */
  borrarCuenta: (password?: string) => Promise<void>;
  /** Se acaba de borrar la cuenta: para despedirse en la pantalla de entrada. */
  cuentaBorrada: boolean;
  logout: () => Promise<void>;
  updateDisplayName: (name: string) => Promise<void>;
  /** Tiene contraseña y aún no ha confirmado su email. */
  emailSinVerificar: boolean;
  /** Vuelve a mandar el correo para confirmar el email. */
  enviarVerificacion: () => Promise<void>;
  /** Pregunta al servidor si ya se confirmó. Devuelve si está verificado. */
  comprobarVerificacion: () => Promise<boolean>;
  /** Manda el correo para poner una contraseña nueva a quien la ha olvidado. */
  recuperarContrasena: (email: string) => Promise<void>;
}

const AuthContext = createContext<AuthContextType | undefined>(undefined);

export const AuthProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const [user, setUser] = useState<User | null>(null);
  const [loading, setLoading] = useState(true);
  const [vinculacionPendiente, setVinculacionPendiente] = useState<VinculacionPendiente | null>(null);
  // La misma, para leerla dentro de las funciones sin depender del render
  const pendienteRef = useRef<VinculacionPendiente | null>(null);
  // Vincular o desvincular cambia el usuario por dentro, no por referencia:
  // este contador obliga a repintar a quien mira sus métodos de acceso.
  const [, setVersion] = useState(0);
  // Tras borrar la cuenta se recarga la página: el aviso llega en la URL
  const cuentaBorrada = new URLSearchParams(window.location.search).has('cuentaBorrada');
  const refrescar = () => setVersion(v => v + 1);

  useEffect(() => {
    const unsubscribe = onAuthStateChanged(auth, (currentUser) => {
      setUser(currentUser);
      setLoading(false);
    });

    return unsubscribe;
  }, []);

  const fijarPendiente = (p: VinculacionPendiente | null) => {
    pendienteRef.current = p;
    setVinculacionPendiente(p);
  };

  /** Si quedó una vinculación a medias y quien entra es esa persona, se completa. */
  const completarVinculacion = useCallback(async (u: User) => {
    const p = pendienteRef.current;
    if (!p || u.email?.toLowerCase() !== p.email.toLowerCase()) return;
    try {
      await linkWithCredential(u, p.credencial);
      refrescar();
    } catch (e) {
      // Que no se pueda vincular no impide entrar: queda la opción de hacerlo
      // a mano desde Configuración → Perfil.
      console.warn('No se pudo vincular el método de acceso pendiente', e);
    }
    fijarPendiente(null);
  }, []);

  /**
   * Apple da el nombre solo la primera vez que alguien entra, y en la web
   * Firebase no lo copia al perfil: si no se guarda ahora, no vuelve. Viene en
   * la respuesta interna del servidor, que no tiene tipo público.
   */
  const guardarNombreDeApple = async (res: UserCredential, nombreNativo?: string) => {
    if (res.providerId !== 'apple.com' || res.user.displayName) return;
    const r = (res as unknown as { _tokenResponse?: Record<string, string> })._tokenResponse ?? {};
    // En la app, el login nativo de iOS ya lo trae aparte
    const nombre = (nombreNativo || r.displayName || [r.firstName, r.lastName].filter(Boolean).join(' ')).trim();
    if (!nombre) return;
    try {
      await updateProfile(res.user, { displayName: nombre });
      // Puede que el perfil aún no exista (lo crea PerfilContext a la vez); si
      // es así, ya lo copiará de displayName al refrescarlo.
      await updateDoc(doc(db, 'usuarios', res.user.uid), { nombre }).catch(() => {});
      refrescar();
    } catch (e) {
      console.warn('No se pudo guardar el nombre de Apple', e);
    }
  };

  const signup = async (email: string, password: string) => {
    const res = await createUserWithEmailAndPassword(auth, email, password);
    // Sin email confirmado, entrar luego con Apple o Google con ese mismo email
    // hace que Firebase se quede solo con ese método y borre la contraseña.
    // Si el correo no sale, la cuenta ya existe: el aviso deja reenviarlo.
    await sendEmailVerification(res.user).catch(e => console.warn('No se pudo enviar el correo de verificación', e));
  };

  const enviarVerificacion = async () => {
    if (!auth.currentUser) throw new Error('No hay sesión activa');
    await sendEmailVerification(auth.currentUser);
  };

  const recuperarContrasena = async (email: string) => {
    await sendPasswordResetEmail(auth, email.trim());
  };

  const comprobarVerificacion = useCallback(async () => {
    const actual = auth.currentUser;
    if (!actual) return false;
    if (actual.emailVerified) return true;
    await actual.reload();
    if (actual.emailVerified) {
      // El token guardado aún dice que no: se renueva para que las reglas lo vean
      await actual.getIdToken(true).catch(() => {});
      refrescar();
    }
    return actual.emailVerified;
  }, []);

  // Tras confirmar el email en el correo, al volver a la app el aviso se va solo
  const actual = auth.currentUser;
  const emailSinVerificar = !!actual && !actual.emailVerified
    && actual.providerData.some(p => p.providerId === 'password');
  useEffect(() => {
    if (!emailSinVerificar) return;
    const alVolver = () => {
      if (document.visibilityState === 'visible') comprobarVerificacion().catch(() => {});
    };
    document.addEventListener('visibilitychange', alVolver);
    return () => document.removeEventListener('visibilitychange', alVolver);
  }, [emailSinVerificar, comprobarVerificacion]);

  const login = async (email: string, password: string) => {
    const res = await signInWithEmailAndPassword(auth, email, password);
    await completarVinculacion(res.user);
  };

  const entrarCon = async (proveedor: ProveedorSocial) => {
    // En la app no hay ventanas emergentes: login nativo de iOS y su token a Firebase
    const nativo = esApp ? await credencialNativa(proveedor) : null;
    try {
      const res = nativo
        ? await signInWithCredential(auth, nativo.credencial)
        : await signInWithPopup(auth, crearProveedor(proveedor));
      await guardarNombreDeApple(res, nativo?.nombre);
      await completarVinculacion(res.user);
    } catch (e: any) {
      if (e?.code === 'auth/account-exists-with-different-credential') {
        const credencial = nativo?.credencial ?? (proveedor === 'google'
          ? GoogleAuthProvider.credentialFromError(e)
          : OAuthProvider.credentialFromError(e));
        const email: string | undefined = e.customData?.email;
        if (credencial && email) fijarPendiente({ email, proveedor, credencial });
      }
      throw e;
    }
  };

  const cancelarVinculacion = () => fijarPendiente(null);

  const vincular = async (proveedor: ProveedorSocial) => {
    if (!auth.currentUser) throw new Error('No hay sesión activa');
    const nativo = esApp ? await credencialNativa(proveedor) : null;
    const res = nativo
      ? await linkWithCredential(auth.currentUser, nativo.credencial)
      : await linkWithPopup(auth.currentUser, crearProveedor(proveedor));
    await guardarNombreDeApple(res, nativo?.nombre);
    refrescar();
  };

  const desvincular = async (providerId: string) => {
    const actual = auth.currentUser;
    if (!actual) throw new Error('No hay sesión activa');
    if (actual.providerData.length <= 1) {
      throw new Error('Es tu único método de acceso: sin él no podrías volver a entrar.');
    }
    await unlink(actual, providerId);
    refrescar();
  };

  /** Apple primero: es el que además obliga a revocar sus tokens al borrar. */
  const metodoReautenticacion = (): MetodoReautenticacion | null => {
    const ids = auth.currentUser?.providerData.map(p => p.providerId) ?? [];
    if (ids.includes('apple.com')) return 'apple';
    if (ids.includes('google.com')) return 'google';
    if (ids.includes('password')) return 'password';
    return null;
  };

  const borrarCuenta = async (password?: string) => {
    const actual = auth.currentUser;
    if (!actual) throw new Error('No hay sesión activa');

    // 1. Volver a identificarse: el servidor solo acepta una sesión recién abierta.
    const metodo = metodoReautenticacion();
    if ((metodo === 'apple' || metodo === 'google') && esApp) {
      const nativo = await credencialNativa(metodo);
      await reauthenticateWithCredential(actual, nativo.credencial);
      if (nativo.codigoApple) {
        await revocarAppleNativo(actual, nativo.codigoApple)
          .catch(e => console.warn('No se pudo revocar el token de Apple', e));
      }
    } else if (metodo === 'apple') {
      const res = await reauthenticateWithPopup(actual, crearProveedor('apple'));
      const cred = OAuthProvider.credentialFromResult(res);
      // Apple exige revocar sus tokens cuando se borra la cuenta. Si fallara,
      // se sigue: dejar la cuenta sin borrar sería peor.
      if (cred?.accessToken) {
        await revokeAccessToken(auth, cred.accessToken)
          .catch(e => console.warn('No se pudo revocar el token de Apple', e));
      }
    } else if (metodo === 'google') {
      await reauthenticateWithPopup(actual, crearProveedor('google'));
    } else {
      if (!password) throw new Error('Escribe tu contraseña para confirmar.');
      await reauthenticateWithCredential(actual, EmailAuthProvider.credential(actual.email ?? '', password));
    }

    // 2. El borrado lo hace el servidor: las reglas no dejan a nadie tocar los
    //    datos de un módulo que no tiene activado, ni borrar su propio perfil.
    const token = await actual.getIdToken(true);
    let respuesta: { ok?: boolean; error?: string } = {};
    try {
      const r = await fetch(URL_BORRAR_CUENTA, {
        method: 'POST',
        headers: { Authorization: `Bearer ${token}` },
      });
      // En local, sin `netlify dev`, esta URL devuelve el index.html con un 200:
      // por eso se mira el cuerpo y no solo el código.
      respuesta = await r.json();
    } catch {
      throw new Error('No se ha podido contactar con el servidor. Inténtalo más tarde.');
    }
    if (!respuesta.ok) throw new Error(respuesta.error || 'No se ha podido borrar la cuenta.');

    fijarPendiente(null);
    await signOut(auth).catch(() => {});
    // La recarga borra el estado: el aviso de despedida va en la URL
    await salirLimpio('/login?cuentaBorrada=1');
  };

  /** Tras cerrar sesión o borrar la cuenta: fuera la caché y página limpia. */
  const salirLimpio = async (destino: string) => {
    await borrarCacheLocal();
    window.location.replace(destino);
  };

  const logout = async () => {
    // Que este iPhone deje de recibir los avisos de quien sale
    if (auth.currentUser) await desactivarNotificaciones(auth.currentUser.uid).catch(() => {});
    await cancelarRecordatorios().catch(() => {});
    await signOut(auth);
    await salirLimpio('/login');
  };

  const updateDisplayName = async (name: string) => {
    if (!auth.currentUser) throw new Error('No hay sesión activa');
    await updateProfile(auth.currentUser, { displayName: name.trim() });
    // Forzar re-render con el displayName actualizado
    setUser({ ...auth.currentUser } as User);
  };

  const value = {
    user,
    loading,
    signup,
    login,
    entrarCon,
    vinculacionPendiente,
    cancelarVinculacion,
    vincular,
    desvincular,
    metodoReautenticacion,
    borrarCuenta,
    cuentaBorrada,
    logout,
    updateDisplayName,
    emailSinVerificar,
    enviarVerificacion,
    comprobarVerificacion,
    recuperarContrasena,
  };

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
};

export const useAuth = () => {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error('useAuth debe ser usado dentro de AuthProvider');
  }
  return context;
};
